"use client";

import { useEffect } from 'react';

export const useIOSViewportFix = () => {
  useEffect(() => {
    // Check if running on iOS
    const isIOS = () => {
      return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    };

    if (!isIOS()) {
      return;
    }

    // Fix viewport height for iOS
    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    // Handle viewport changes
    const handleViewportChange = () => {
      setViewportHeight();

      // Prevent document body from scrolling
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      document.body.style.overflow = 'auto';
      (document.body.style as any).webkitOverflowScrolling = 'touch';
    };

    // Prevent elastic scrolling
    const preventElasticScroll = (e: TouchEvent) => {
      const target = e.target as HTMLElement;

      // Allow scrolling for input elements
      if (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT') {
        return;
      }

      // Check if the element or its parent has scrollable content
      let element: HTMLElement | null = target;
      while (element) {
        const { scrollHeight, clientHeight, scrollTop } = element;
        const isScrollable = scrollHeight > clientHeight;

        if (isScrollable) {
          const isAtTop = scrollTop === 0;
          const isAtBottom = scrollTop + clientHeight >= scrollHeight;

          // Prevent scroll beyond boundaries
          if ((isAtTop && e.touches[0].clientY > (e as any).startY) ||
              (isAtBottom && e.touches[0].clientY < (e as any).startY)) {
            e.preventDefault();
          }
          return;
        }

        element = element.parentElement;
      }
    };

    // Store initial touch position
    const handleTouchStart = (e: TouchEvent) => {
      (e as any).startY = e.touches[0].clientY;
    };

    // Fix for iOS keyboard viewport shift
    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT') {
        // Prevent zoom on input focus
        const meta = document.querySelector('meta[name="viewport"]');
        if (meta) {
          meta.setAttribute('content',
            'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0');
        }

        // Scroll element into view smoothly
        setTimeout(() => {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
    };

    const handleFocusOut = () => {
      // Restore viewport settings
      const meta = document.querySelector('meta[name="viewport"]');
      if (meta) {
        meta.setAttribute('content',
          'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0');
      }

      // Scroll to top to hide keyboard completely
      window.scrollTo(0, 0);
    };

    // Visual viewport API for iOS 13+
    const handleVisualViewportChange = () => {
      if ('visualViewport' in window) {
        const viewport = window.visualViewport;
        if (viewport) {
          document.documentElement.style.setProperty('--keyboard-height',
            `${window.innerHeight - viewport.height}px`);
        }
      }
    };

    // Initial setup
    setViewportHeight();
    handleViewportChange();

    // Add event listeners
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('orientationchange', handleViewportChange);
    document.addEventListener('touchstart', handleTouchStart, { passive: false });
    document.addEventListener('touchmove', preventElasticScroll, { passive: false });
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    if ('visualViewport' in window && window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportChange);
      window.visualViewport.addEventListener('scroll', handleVisualViewportChange);
    }

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', preventElasticScroll);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);

      if ('visualViewport' in window && window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleVisualViewportChange);
        window.visualViewport.removeEventListener('scroll', handleVisualViewportChange);
      }
    };
  }, []);
};

export default useIOSViewportFix;